import {TimeTableItem} from '../BusinessScheduleItemInterfaces'
import {BusinessScheduleSettings} from './BusinessScheduleInput'
import {Nullable} from '../../../../../app/TypeHelpers'
import {
    CHARGE_UNIT,
    HOURS_24,
    RESERVATION_TIME_UNIT_MINUTES,
    UNIT_OF_BUSINESS_SCHEDULE,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../../../../CodeMaster'
import {compare} from '../BusinessScheduleTimeHelper'
import {createUUID} from '../../../../../stringHelper/stringHelpers'
import {Messages} from '../../../../../Messages'

export function isValidJoinPrevDaysSchedule(
    joinPrevDaysSchedule: VALID_OR_NOT,
    businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE,
): boolean {
    return joinPrevDaysSchedule === VALID_OR_NOT.VALID &&
        businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY &&
        businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS &&
        businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE
}

export function checkBusinessScheduleSettings(settings: BusinessScheduleSettings): boolean {
    if (settings.businessScheduleUnit === null) {
        return false
    }

    if (!isValidJoinPrevDaysSchedule(settings.joinPrevDaysSchedule, settings.businessScheduleUnit)) {
        if (settings.charge.charge === null) {
            return false
        }

        if (settings.charge.fixedCharge === false) {
            if (settings.charge.chargeUnitTime === null) {
                return false
            }
        }

        if (settings.charge.fixedCharge === false &&
            (
                settings.changedChargeApplyStartTime !== null ||
                settings.changedCharge.charge !== null ||
                settings.changedCharge.chargeUnitTime !== null
            )
        ) {
            if (settings.changedChargeApplyStartTime === null ||
                settings.changedCharge.charge === null) {
                return false
            }

            if (settings.changedCharge.fixedCharge === false) {
                if (settings.changedCharge.chargeUnitTime === null) {
                    return false
                }
            }
        }
    }

    if (settings.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE) {
        if (settings.startTimeHour === null ||
            settings.startTimeMinute === null ||
            settings.endTimeHour === null ||
            settings.endTimeMinute === null) {
            return false
        }
    }

    return true
}

export function makeTimeTableItem(settings: BusinessScheduleSettings): Nullable<TimeTableItem> {
    if (settings.businessScheduleUnit === null) {
        return null
    }

    const timeTableItem: TimeTableItem = {
        id: createUUID(),
        title: '営業',
        businessScheduleUnit: settings.businessScheduleUnit,
        dateStraddle: null,
        joinPrevDaysSchedule: null,
        startTime: null,
        endTime: null,
        charge: null,
        chargeUnit: null,
        chargeUnitTime: null,
        changedChargeApplyStartTime: null,
        changedChargeApplyStartTimeUnit: null,
        changedCharge: null,
        changedChargeUnit: null,
        changedChargeUnitTime: null,
    }

    if (!isValidJoinPrevDaysSchedule(settings.joinPrevDaysSchedule, settings.businessScheduleUnit)) {
        timeTableItem.charge = settings.charge.charge

        if (settings.charge.fixedCharge) {
            timeTableItem.chargeUnit = CHARGE_UNIT.FIXED
        } else {
            timeTableItem.chargeUnit = settings.charge.chargeUnit === UNIT_TIME.HOUR ?
                CHARGE_UNIT.TIME : CHARGE_UNIT.MINUTE
            timeTableItem.chargeUnitTime = settings.charge.chargeUnitTime

            if (settings.changedChargeApplyStartTime !== null) {
                timeTableItem.changedChargeApplyStartTime = settings.changedChargeApplyStartTime
                timeTableItem.changedChargeApplyStartTimeUnit = settings.changedChargeApplyStartTimeUnit
                timeTableItem.changedCharge = settings.changedCharge.charge

                if (settings.changedCharge.fixedCharge) {
                    timeTableItem.changedChargeUnit = CHARGE_UNIT.FIXED
                } else {
                    timeTableItem.changedChargeUnit = settings.changedCharge.chargeUnit === UNIT_TIME.HOUR ?
                        CHARGE_UNIT.TIME : CHARGE_UNIT.MINUTE
                    timeTableItem.changedChargeUnitTime = settings.changedCharge.chargeUnitTime
                }
            }
        }
    }

    if (settings.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE) {
        if (settings.startTimeHour === null ||
            settings.startTimeMinute === null ||
            settings.endTimeHour === null ||
            settings.endTimeMinute === null
        ) {
            return null
        }
        timeTableItem.startTime = {
            hour: settings.startTimeHour,
            minute: settings.startTimeMinute,
        }
        timeTableItem.endTime = {
            hour: settings.endTimeHour,
            minute: settings.endTimeMinute,
        }
    }

    if (settings.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS) {
        timeTableItem.dateStraddle = settings.dateStraddle
    }

    if (settings.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS &&
        settings.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE &&
        settings.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY) {
        timeTableItem.joinPrevDaysSchedule = settings.joinPrevDaysSchedule
    }

    return timeTableItem
}

export function checkNewTimeTableItem(newItem: TimeTableItem, items: TimeTableItem[]): Nullable<string> {
    if (newItem.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE) {
        if (newItem.startTime === null || newItem.endTime === null) {
            throw new Error('newItem.startTime or newItem.endTime is not set')
        }

        if (isDateStraddle(newItem)) {
            const endTimeHour = HOURS_24.codeToEnumConstants(newItem.endTime.hour.value)
            if (endTimeHour === HOURS_24.VOL_0 &&
                newItem.endTime.minute === RESERVATION_TIME_UNIT_MINUTES.VOL_0) {
                return Messages.EFACA0021.toString()
            }

            if (compare(newItem.startTime, newItem.endTime) <= 0) {
                return Messages.EFACA0003.toString()
            }
        } else {
            if (compare(newItem.startTime, newItem.endTime) >= 0) {
                return Messages.EOPCC0025.toString('営業終了時間', '営業開始時間')
            }
        }
    }

    if (!checkSchedulePatternIsNotMixed(newItem, items)) {
        return Messages.EFACA0027.toString()
    }

    if (!checkScheduleHolidayAndJoinedPrevIsNotMixed(newItem, items)) {
        return Messages.EFACA0028.toString()
    }

    if (!checkScheduleIsNotOverlapped(newItem, items)) {
        return Messages.EFACA1002.toString('営業開始終了時間')
    }

    return null
}

function checkSchedulePatternIsNotMixed(newItem: TimeTableItem, items: TimeTableItem[]): boolean {
    const newUnit = newItem.businessScheduleUnit
    let notMixed: boolean
    if (isDayOfTheWeek(newUnit)) {
        notMixed = items.every(item => isDayOfTheWeek(item.businessScheduleUnit))
    } else {
        notMixed = items.every(item => newUnit === item.businessScheduleUnit)
    }
    return notMixed
}

function isDayOfTheWeek(unitOfBusinessSchedule: UNIT_OF_BUSINESS_SCHEDULE): boolean {
    return unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.SUNDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.MONDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.TUESDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.WEDNESDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.THURSDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.FRIDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.SATURDAY ||
        unitOfBusinessSchedule === UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY
}

function checkScheduleHolidayAndJoinedPrevIsNotMixed(newItem: TimeTableItem, items: TimeTableItem[]): boolean {
    const newUnit = newItem.businessScheduleUnit
    if (!isDayOfTheWeek(newUnit)) {
        return true
    }

    let notMixed: boolean = true
    if (newUnit === UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY) {
        notMixed = items.every(item => item.joinPrevDaysSchedule !== VALID_OR_NOT.VALID)
    } else if (newItem.joinPrevDaysSchedule === VALID_OR_NOT.VALID) {
        notMixed = items.every(item => item.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY)
    }

    return notMixed
}

function checkScheduleIsNotOverlapped(newItem: TimeTableItem, items: TimeTableItem[]): boolean {
    return items.every(item => !isOverlapped(newItem, item))
}

function isOverlapped(item1: TimeTableItem, item2: TimeTableItem): boolean {
    if (item1.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE ||
        item2.businessScheduleUnit === UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE
    ) {
        return true
    }

    if (isDayOfTheWeek(item1.businessScheduleUnit) &&
        isDayOfTheWeek(item2.businessScheduleUnit) &&
        item1.businessScheduleUnit !== item2.businessScheduleUnit
    ) {
        return false
    }

    if (!isDateStraddle(item1) && !isDateStraddle(item2)) {
        if (compare(item1.endTime, item2.startTime) <= 0 ||
            compare(item2.endTime, item1.startTime) <= 0
        ) {
            return false
        }
    } else if (
        isDateStraddle(item1) && !isDateStraddle(item2) ||
        !isDateStraddle(item1) && isDateStraddle(item2)
    ) {
        if (compare(item1.endTime, item2.startTime) <= 0 &&
            compare(item2.endTime, item1.startTime) <= 0
        ) {
            return false
        }
    }
    return true
}

function isDateStraddle(item: TimeTableItem): boolean {
    return item.dateStraddle === VALID_OR_NOT.VALID
}
